import { FC, useState } from 'react';
import { Box, Stack } from '@mui/material';
import {
  DataGrid,
  DataGridProps,
  GridApi,
  GridCallbackDetails,
  GridColumnsInitialState,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';
import { TableSearchChangeCallback } from '~/pages/ProjectDetailsPage/tabs/WorkbenchTab/types';
import { AppliedFiltersBar, TableColumnHeader, TableNoRows, TablePagination, TablePanel } from './components';
import TableFilter from './components/TableFilter';
import { TableFilterContextProvider, useTableFilters } from './components/context';
import { TableFilterContextProviderProps } from './components/context/TableFilterContextProvider';
import { useGridInfiniteScroll } from './hooks';

export const PAGE_SIZE = 10;
export const MAX_PAGE_SIZE = 100;
const COLUMN_HEADER_HEIGHT = 56;

export interface TableProps extends DataGridProps, Omit<TableFilterContextProviderProps, 'children'> {
  columnsInitialState?: GridColumnsInitialState;
  onSearchChange?: TableSearchChangeCallback;
  onSortChange?: (model: GridSortModel, details: GridCallbackDetails) => void;
  searchPlaceholder?: string;
  searchWidth?: string;
  initialSearchText?: string;
  onLoadMore?: () => void;
  hasNextPage?: boolean;
  wrapperHeight?: string;
}

const FilterSidebar = ({ height }: { height?: number | string }) => {
  const { open, closeFilters } = useTableFilters();

  return <TableFilter open={open} onClose={closeFilters} height={height} />;
};

const Table: FC<TableProps> = ({
  isFiltersLoading,
  onFiltersChange,
  onSearchChange,
  onSortChange,
  columns,
  filterMode,
  searchPlaceholder,
  searchWidth,
  initialFiltersModel,
  initialSearchText,
  hasNextPage,
  onLoadMore,
  wrapperHeight,
  ...props
}) => {
  const apiRef = useGridApiRef<GridApi>();
  const [filtersBarHeight, setFiltersBarHeight] = useState<number>(0);

  useGridInfiniteScroll({ apiRef, hasNextPage, onLoadMore, loading: props.loading });

  const getFooterElement = (): HTMLElement =>
    (apiRef.current.rootElementRef?.current?.querySelector<HTMLDivElement>('.MuiDataGrid-footerContainer') ??
      document.body) as HTMLElement;

  const scrollToTop = () => {
    const virtualScroller = apiRef.current.rootElementRef?.current?.querySelector('.MuiDataGrid-virtualScroller');
    virtualScroller?.scrollTo({ top: 0 });
  };

  const handleSortChange = (model: GridSortModel, details: GridCallbackDetails) => {
    scrollToTop();
    onSortChange?.(model, details);
  };

  const shouldRenderTablePanel = filterMode === 'server' && props.filterOptions && onFiltersChange && onSearchChange;

  return (
    <Stack
      direction="column"
      sx={{
        display: 'flex',
        height: 'fit-content',
        width: '100%',
      }}
    >
      <TableFilterContextProvider
        onFiltersChange={onFiltersChange}
        filterOptions={props.filterOptions}
        isFiltersLoading={isFiltersLoading}
        initialFiltersModel={initialFiltersModel}
        resultsCount={props.rowCount}
      >
        {shouldRenderTablePanel && (
          <TablePanel
            onSearchChange={onSearchChange}
            hideFooter={props.hideFooter}
            searchPlaceholder={searchPlaceholder}
            searchWidth={searchWidth}
            initialSearchText={initialSearchText}
          />
        )}
        <Stack direction="row" sx={{ alignItems: 'stretch' }}>
          {shouldRenderTablePanel && <FilterSidebar height={wrapperHeight} />}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              ...(wrapperHeight ? { height: wrapperHeight, minHeight: wrapperHeight } : {}),
            }}
          >
            {shouldRenderTablePanel && <AppliedFiltersBar apiRef={apiRef} onHeightChange={setFiltersBarHeight} />}
            <DataGrid
              apiRef={apiRef}
              slots={{
                pagination: TablePagination,
                noRowsOverlay: TableNoRows,
              }}
              slotProps={{
                panel: {
                  anchorEl: getFooterElement,
                },
              }}
              columns={columns.map((column) => ({
                flex: 1,
                renderHeader: TableColumnHeader,
                ...column,
              }))}
              // if no pagination required, this value should be set manually
              initialState={{
                pagination: { paginationModel: { page: 0, pageSize: PAGE_SIZE } },
                columns: { ...props.columnsInitialState },
                sorting: { sortModel: props.sortModel },
              }}
              disableColumnFilter={filterMode !== 'client'}
              onSortModelChange={handleSortChange}
              {...props}
              columnHeaderHeight={COLUMN_HEADER_HEIGHT + filtersBarHeight}
              sx={[
                {
                  '& .MuiDataGrid-columnHeaders': { position: 'relative' },
                  '& .MuiDataGrid-columnHeader': { alignItems: 'flex-start' },
                  // keep the titles in the top strip so the overlaid bar sits below them
                  '& .MuiDataGrid-columnHeaderDraggableContainer, & .MuiDataGrid-columnHeaderTitleContainer': {
                    height: COLUMN_HEADER_HEIGHT,
                  },
                },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
              ]}
            />
          </Box>
        </Stack>
      </TableFilterContextProvider>
    </Stack>
  );
};

export default Table;
